import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CalendarToday as TodayIcon,
  EventNote as ReportIcon,
  Timer as HoursIcon,
  History as StaleIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTaskOverlay } from "../../utils/TaskOverlayContext";
import { formatFloatToTimeHM } from "../../utils/timeFormatter";

const fmtDate = (d) => (d ? dayjs(d).format("ddd, MMM D") : "—");

/** Label + value pair used for each stat in the strip. */
const Stat = ({ icon, label, children, color = "text.primary" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
    <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color, lineHeight: 1.3 }} noWrap>
        {children}
      </Typography>
    </Box>
  </Box>
);

/**
 * The biweekly strip at the top of the dashboard.
 *
 * Shows today's date, when the next biweekly report is due (the Friday before
 * the next period starts), the hours the user has logged in the running
 * period, whether their report for it is done, and how many of their open
 * tasks are still parked in a sprint that has already ended.
 *
 * @param {Object} props
 * @param {Object|null} props.data Payload of `dashboard/biweekly-topline/`.
 */
const BiweeklyTopline = ({ data }) => {
  const navigate = useNavigate();
  const { openTaskDetail } = useTaskOverlay();

  if (!data) return null;

  const { today, period, report_due, days_until_due, hours_logged, report, report_completed, stale_tasks } = data;
  const dueSoon = days_until_due != null && days_until_due <= 2;
  const staleCount = stale_tasks?.count || 0;

  const reportLabel = report_completed
    ? report.status
    : report
      ? `${report.status} — not submitted`
      : "Not started";

  const dueLabel =
    days_until_due == null
      ? ""
      : days_until_due === 0
        ? " (today)"
        : days_until_due < 0
          ? ` (${-days_until_due}d overdue)`
          : ` (in ${days_until_due}d)`;

  const staleTooltip = staleCount ? (
    <Box>
      {stale_tasks.tasks.map((t) => (
        <Typography key={t.id} variant="caption" sx={{ display: "block" }}>
          {t.name} · {t.sprint} (ended {fmtDate(t.sprint_end)})
        </Typography>
      ))}
      {staleCount > stale_tasks.tasks.length && (
        <Typography variant="caption" sx={{ display: "block", fontStyle: "italic" }}>
          +{staleCount - stale_tasks.tasks.length} more
        </Typography>
      )}
    </Box>
  ) : (
    "All your open tasks are in the current or upcoming sprints"
  );

  return (
    <Paper
      variant="outlined"
      sx={{ px: 2, py: 1.5, mb: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3 }}
    >
      <Stat icon={<TodayIcon fontSize="small" />} label="Today">
        {fmtDate(today)}
      </Stat>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

      <Stat
        icon={<ReportIcon fontSize="small" />}
        label="Next biweekly report due"
        color={dueSoon && !report_completed ? "warning.main" : "text.primary"}
      >
        {fmtDate(report_due)}
        {dueLabel}
      </Stat>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

      <Stat
        icon={<HoursIcon fontSize="small" />}
        label={`Hours this biweekly${period?.name ? ` · ${period.name}` : ""}`}
      >
        {formatFloatToTimeHM(hours_logged || 0)}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {fmtDate(period?.start)} – {fmtDate(period?.end)}
        </Typography>
      </Stat>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

      <Stat
        icon={<StaleIcon fontSize="small" />}
        label="Tasks still in previous sprints"
        color={staleCount ? "warning.main" : "success.main"}
      >
        <Tooltip title={staleTooltip} arrow>
          <Box component="span" sx={{ cursor: staleCount ? "pointer" : "default" }}>
            {staleCount}
          </Box>
        </Tooltip>
      </Stat>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
        <Chip
          size="small"
          label={reportLabel}
          color={report_completed ? "success" : dueSoon ? "warning" : "default"}
          variant={report_completed ? "filled" : "outlined"}
        />
        {!report_completed && (
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate("/reports/biweekly-review")}
          >
            {report ? "Finish report" : "Start report"}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default BiweeklyTopline;
